import { useEffect, useMemo, useState } from "react";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useAuth } from "../auth/AuthProvider";
import { useI18n } from "../i18n";
import { formatBillingError } from "../lib/billingErrors";
import { confirmStripeCheckout } from "../lib/stripeCheckout";
import type { BillingPlan, BillingPlansResponse, BillingQuotaEvent } from "../types/api";

// Cache the Stripe.js loader per publishable key so we don't re-init it on
// every render (loadStripe returns the same promise for the same key).
const stripeCache = new Map<string, Promise<Stripe | null>>();
const zhPlanLabels = ["体验", "日常", "专业"];

function stripeFor(publishableKey: string): Promise<Stripe | null> {
  let p = stripeCache.get(publishableKey);
  if (!p) {
    p = loadStripe(publishableKey);
    stripeCache.set(publishableKey, p);
  }
  return p;
}

function dateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

export function BillingModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { client } = useAuth();
  const { t, lang } = useI18n();
  const [info, setInfo] = useState<BillingPlansResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<BillingPlan | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // (Re)load plans + current balance whenever the modal opens.
  useEffect(() => {
    if (!open || !client) return;
    setError(null);
    setSelected(null);
    setClientSecret(null);
    setDone(false);
    client
      .getBillingPlans()
      .then(setInfo)
      .catch((e) => setError(formatBillingError(e, t, t("billing.loadFailed"))));
  }, [open, client, t]);

  const stripePromise = useMemo(
    () => (info?.publishableKey ? stripeFor(info.publishableKey) : null),
    [info?.publishableKey]
  );

  if (!open) return null;

  async function choosePlan(plan: BillingPlan) {
    if (!client) return;
    setError(null);
    setSelected(plan);
    try {
      const { clientSecret: cs } = await client.createPaymentIntent(plan.id);
      if (!cs) throw new Error("missing payment client secret");
      setClientSecret(cs);
    } catch (e) {
      setSelected(null);
      setError(formatBillingError(e, t, t("billing.failed")));
    }
  }

  function backToPlans() {
    setSelected(null);
    setClientSecret(null);
    setError(null);
  }

  async function onPaid() {
    setDone(true);
    // The webhook tops up the balance server-side; re-fetch a couple times so
    // the displayed balance catches up once it lands.
    for (let i = 0; i < 5 && client; i += 1) {
      await new Promise((r) => setTimeout(r, 1500));
      try {
        setInfo(await client.getBillingPlans());
      } catch {
        /* ignore */
      }
    }
  }

  const fmt = (n: number) => {
    const currency = (info?.currency || "cny").toUpperCase();
    if (currency === "CNY" || currency === "CNH" || currency === "RMB") return `¥${n.toFixed(2)}`;
    if (currency === "USD") return `$${n.toFixed(2)}`;
    return `${currency} ${n.toFixed(2)}`;
  };

  const eventSource = (event: BillingQuotaEvent) =>
    event.source === "payment" ? t("billing.history.payment") : t("billing.history.admin");

  const balanceAfter = (event: BillingQuotaEvent) => Math.max(0, event.quotaAfterUsd - event.quotaUsedUsd);

  return (
    <div className="modal-root" role="dialog" aria-modal="true" aria-label={t("billing.title")}>
      <div className="modal-scrim" onClick={onClose} />
      <div className="modal-card">
        <div className="modal-head">
          <span className="modal-title">{t("billing.title")}</span>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t("common.close")} title={t("common.close")}>
            ×
          </button>
        </div>
        <div className="modal-body">
          {info && !info.enabled ? <p className="muted">{t("billing.notConfigured")}</p> : null}

          {info && info.enabled ? (
            <>
              <p className="muted settings-hint">
                {t("billing.balance")}: {fmt(info.balanceUsd)}
              </p>

              {done ? (
                <div className="ok-banner">{t("billing.success")}</div>
              ) : !selected || !clientSecret ? (
                <>
                  <p className="subtitle">{t("billing.subtitle")}</p>
                  <div className="plan-list">
                    {info.plans.map((plan, idx) => (
                      <button
                        key={plan.id}
                        type="button"
                        className="plan-card"
                        onClick={() => void choosePlan(plan)}
                        disabled={Boolean(selected)}
                      >
                        <span className="plan-name">{lang === "zh" ? (zhPlanLabels[idx] ?? plan.label) : plan.label}</span>
                        <span className="plan-price">{fmt(plan.priceUsd)}</span>
                        <span className="plan-quota muted">{t("billing.addQuota", { quota: fmt(plan.quotaUsd) })}</span>
                      </button>
                    ))}
                  </div>
                </>
              ) : stripePromise ? (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <CheckoutForm
                    payLabel={t("billing.payNow", { price: fmt(selected.priceUsd) })}
                    backLabel={t("billing.back")}
                    onPaid={() => void onPaid()}
                    onBack={backToPlans}
                    onError={setError}
                  />
                </Elements>
              ) : null}
            </>
          ) : null}

          {info ? (
            <section className="quota-history">
              <h3>{t("billing.history.title")}</h3>
              {info.quotaEvents.length ? (
                <div className="quota-history-list">
                  {info.quotaEvents.map((event) => (
                    <div key={event.id} className="quota-history-item">
                      <div>
                        <strong>{eventSource(event)}</strong>
                        <span>{dateTime(event.createdAt)}</span>
                      </div>
                      <div>
                        <span className={event.amountUsd >= 0 ? "quota-delta positive" : "quota-delta negative"}>
                          {event.amountUsd >= 0 ? "+" : "-"}{fmt(Math.abs(event.amountUsd))}
                        </span>
                        <span className="muted">{t("billing.history.balanceAfter", { balance: fmt(balanceAfter(event)) })}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="muted settings-hint">{t("billing.history.empty")}</p>
              )}
            </section>
          ) : null}

          {error ? <div className="error-banner">{error}</div> : null}
        </div>
      </div>
    </div>
  );
}

// The actual card form. Lives inside <Elements> so it can use the Stripe hooks.
function CheckoutForm({
  payLabel,
  backLabel,
  onPaid,
  onBack,
  onError
}: {
  payLabel: string;
  backLabel: string;
  onPaid: () => void;
  onBack: () => void;
  onError: (msg: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);

  async function pay() {
    if (!stripe || !elements) return;
    setBusy(true);
    onError("");
    try {
      await confirmStripeCheckout({
        stripe,
        elements,
        returnUrl: window.location.href,
        onPaid,
        onError,
        t
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="checkout-form">
      <PaymentElement />
      <button type="button" className="btn-primary settings-save" onClick={() => void pay()} disabled={busy || !stripe}>
        {busy ? <span className="spinner inline" /> : payLabel}
      </button>
      <button type="button" className="link-btn" onClick={onBack} disabled={busy}>
        {backLabel}
      </button>
    </div>
  );
}
